// frontend/src/store/useMapStore.js
import { create } from "zustand";
import useGameStore from "./useGameStore";
import { EMPTY_ARRAY } from "@utils/constants";

const useMapStore = create((set) => ({
  // UI STATE ONLY - Map interaction state
  hoveredTile: null,
  activeTile: null,
  focusTile: null, // Tile highlighted from UI elements (like CoordinateLink)
  interactionMode: 'select', // Default to select mode as per documentation
  setInteractionMode: (mode) => set({ interactionMode: mode }),

  // Visual state for map overlays
  visual: {
    highlightTiles: [],     // Tiles highlighted for targeting
    actionPaths: [],        // Action line paths
    conflictTiles: [],      // Conflict indicators
    overlayMode: null,      // Current overlay mode
    aoePreviewTiles: [],    // AOE preview tiles (shown on hover)
    aoePreviewUnits: [],    // Unit IDs that will be affected by AOE
    aoePreviewBeneficial: false, // Is AOE beneficial (blue) or harmful (orange)
    brokenShields: [],      // Unit IDs whose shields broke during cinematic (hardcode for visual)
    processingTiles: [],    // Tiles with pending API calls {x, y, type: 'deploy'|'action'}
  },

  setHoveredTile: (tile) => set({ hoveredTile: tile }),
  setActiveTile:   (tile) => set({ activeTile: tile }),
  setFocusTile:    (tile) => set({ focusTile: tile }),
  
  setHighlightTiles: (tiles) =>
    set((s) => ({ visual: { ...s.visual, highlightTiles: tiles } })),

  setActionPaths: (paths) =>
    set((s) => ({ visual: { ...s.visual, actionPaths: paths } })),
  setConflictTiles: (tiles) =>
    set((s) => ({ visual: { ...s.visual, conflictTiles: tiles } })),
  setOverlayMode: (mode) =>
    set((s) => ({ visual: { ...s.visual, overlayMode: mode } })),

  // AOE Preview - shows area of effect on hover
  setAoePreview: (tiles, unitIds = [], isBeneficial = false) =>
    set((s) => ({
      visual: {
        ...s.visual,
        aoePreviewTiles: tiles,
        aoePreviewUnits: unitIds,
        aoePreviewBeneficial: isBeneficial
      }
    })),
  clearAoePreview: () =>
    set((s) => ({
      visual: {
        ...s.visual,
        aoePreviewTiles: [],
        aoePreviewUnits: [],
        aoePreviewBeneficial: false
      }
    })),

  // Broken shields tracking (hardcode for cinematic visual)
  addBrokenShield: (unitId) =>
    set((s) => ({
      visual: {
        ...s.visual,
        brokenShields: s.visual.brokenShields.includes(unitId)
          ? s.visual.brokenShields
          : [...s.visual.brokenShields, unitId]
      }
    })),
  clearBrokenShields: () =>
    set((s) => ({
      visual: {
        ...s.visual,
        brokenShields: []
      }
    })),

  // Processing tiles - shows loading indicator while API call is pending
  addProcessingTile: (x, y, type = 'action') =>
    set((s) => ({
      visual: {
        ...s.visual,
        processingTiles: [...s.visual.processingTiles, { x, y, type }]
      }
    })),
  removeProcessingTile: (x, y) =>
    set((s) => ({
      visual: {
        ...s.visual,
        processingTiles: s.visual.processingTiles.filter(
          (t) => !(t.x === x && t.y === y)
        )
      }
    })),
  isProcessingTile: (x, y) => {
    const state = useMapStore.getState();
    return state.visual.processingTiles.some((t) => t.x === x && t.y === y);
  },

  clearVisuals: () =>
    set((s) => ({
      visual: {
        ...s.visual,
        highlightTiles: [],
        actionPaths: [],
        conflictTiles: [],
        animation: [],
        aoePreviewTiles: [],
        aoePreviewUnits: [],
        aoePreviewBeneficial: false,
        brokenShields: [],
      },
    })),

  selectTerrainTiles: () => {
    const layout = useGameStore.getState().game?.map?.layout;
    if (!layout || !Array.isArray(layout)) {
      return EMPTY_ARRAY;
    }

    const tiles = [];
    for (let y = 0; y < layout.length; y++) {
      for (let x = 0; x < layout[y].length; x++) {
        tiles.push({ x, y, terrain: layout[y][x] });
      }
    }
    return tiles;
  },

  // Center the map viewport on a specific tile
  centerOnTile: (x, y, tileSize) => {
    const mapArea = document.querySelector('.map-area');
    if (!mapArea) return;

    // Calculate tile center position in pixels
    const tileX = x * tileSize + tileSize / 2;
    const tileY = y * tileSize + tileSize / 2;

    // Calculate scroll to center the tile in viewport
    const scrollLeft = tileX - mapArea.clientWidth / 2;
    const scrollTop = tileY - mapArea.clientHeight / 2;

    mapArea.scrollTo({
      left: Math.max(0, scrollLeft),
      top: Math.max(0, scrollTop),
      behavior: 'smooth'
    });
  },

}));

export default useMapStore;
